import type { ComponentProps, ReactNode } from "react";
import M360StructuredBlockView from "../M360StructuredBlock";
import { type M360StructuredBlock, upgradeAgentStructuredBlock } from "../m360StructuredContent";
import type { RecordListItemFields } from "./cardDisplayUtils";
import {
  AlertBanner,
  ChartDashboard,
  ChartPanel,
  CommitteeCard,
  CountSummaryCard,
  DataTable,
  EventCard,
  EventList,
  FacilityCard,
  InteractionList,
  JobPostCard,
  JobPostList,
  MembershipStatusCard,
  MetricRow,
  NoteCard,
  NoteList,
  OrderSummaryCard,
  PdrCreditRow,
  PdrProgressCard,
  RegistrationCard,
  RosterActivityCard,
  SectionCard,
  StatTile,
  TaskCard
} from "./cards";

type RegistryComponent = (props: Record<string, unknown> & { children?: ReactNode }) => ReactNode;

function asPersonBlock(props: Record<string, unknown>): Extract<M360StructuredBlock, { type: "person" }> {
  return {
    type: "person",
    pgaId: String(props.pgaId),
    name: String(props.name),
    subtitle: props.subtitle ? String(props.subtitle) : undefined,
    imageUrl: props.imageUrl ? String(props.imageUrl) : undefined,
    links: Array.isArray(props.links)
      ? props.links.map((link) => {
          const item = link as { label: string; href: string; description?: string | null };
          return {
            label: item.label,
            href: item.href,
            description: item.description ?? undefined
          };
        })
      : undefined
  };
}

function asRecordListBlock(props: Record<string, unknown>): Extract<M360StructuredBlock, { type: "record_list" }> {
  const items = Array.isArray(props.items) ? props.items : [];
  return {
    type: "record_list",
    title: props.title ? String(props.title) : undefined,
    items: items.map((item) => {
      const row = item as RecordListItemFields;
      return {
        title: row.title,
        subtitle: row.subtitle ?? undefined,
        meta: row.meta ?? undefined,
        href: row.href ?? undefined,
        imageUrl: row.imageUrl ?? undefined,
        kind: row.kind ?? undefined,
        facilityName: row.facilityName ?? undefined,
        facilityHref: row.facilityHref ?? undefined,
        location: row.location ?? undefined,
        links: row.links ?? undefined
      };
    })
  };
}

function asInteractionBlock(props: Record<string, unknown>): Extract<M360StructuredBlock, { type: "interaction" }> {
  return {
    type: "interaction",
    pgaId: String(props.pgaId),
    interactionId: String(props.interactionId),
    title: String(props.title),
    subtitle: props.subtitle ? String(props.subtitle) : undefined,
    meta: props.meta ? String(props.meta) : undefined
  };
}

function asLinksBlock(props: Record<string, unknown>): Extract<M360StructuredBlock, { type: "links" }> {
  const links = Array.isArray(props.links) ? props.links : [];
  return {
    type: "links",
    title: props.title ? String(props.title) : undefined,
    links: links.map((link) => {
      const item = link as { label: string; href: string; description?: string | null };
      return {
        label: item.label,
        href: item.href,
        description: item.description ?? undefined
      };
    })
  };
}

function bind<T extends Record<string, unknown>>(Component: (props: T) => ReactNode) {
  return (props: Record<string, unknown>) => <Component {...(props as T)} />;
}

/** Maps json-render catalog component names to M360 React implementations. */
export const agentChatRegistry: Record<string, RegistryComponent> = {
  Stack: ({ children, gap }) => <div className={gap === "sm" ? "space-y-2" : "space-y-3"}>{children}</div>,
  MetricRow: ({ children }) => <MetricRow>{children}</MetricRow>,
  ChartDashboard: (props) => <ChartDashboard title={props.title as string | null}>{props.children}</ChartDashboard>,
  PersonCard: (props) => <M360StructuredBlockView block={asPersonBlock(props)} />,
  RecordList: (props) => <M360StructuredBlockView block={upgradeAgentStructuredBlock(asRecordListBlock(props))} />,
  InteractionCard: (props) => <M360StructuredBlockView block={asInteractionBlock(props)} />,
  LinkChips: (props) => <M360StructuredBlockView block={asLinksBlock(props)} />,
  FacilityCard: bind<ComponentProps<typeof FacilityCard>>(FacilityCard),
  SectionCard: bind<ComponentProps<typeof SectionCard>>(SectionCard),
  JobPostCard: bind<ComponentProps<typeof JobPostCard>>(JobPostCard),
  JobPostList: bind<ComponentProps<typeof JobPostList>>(JobPostList),
  EventList: bind<ComponentProps<typeof EventList>>(EventList),
  EventCard: bind<ComponentProps<typeof EventCard>>(EventCard),
  RegistrationCard: bind<ComponentProps<typeof RegistrationCard>>(RegistrationCard),
  CommitteeCard: bind<ComponentProps<typeof CommitteeCard>>(CommitteeCard),
  NoteCard: bind<ComponentProps<typeof NoteCard>>(NoteCard),
  NoteList: bind<ComponentProps<typeof NoteList>>(NoteList),
  InteractionList: bind<ComponentProps<typeof InteractionList>>(InteractionList),
  TaskCard: bind<ComponentProps<typeof TaskCard>>(TaskCard),
  MembershipStatusCard: bind<ComponentProps<typeof MembershipStatusCard>>(MembershipStatusCard),
  CountSummaryCard: bind<ComponentProps<typeof CountSummaryCard>>(CountSummaryCard),
  StatTile: bind<ComponentProps<typeof StatTile>>(StatTile),
  AlertBanner: bind<ComponentProps<typeof AlertBanner>>(AlertBanner),
  PdrProgressCard: bind<ComponentProps<typeof PdrProgressCard>>(PdrProgressCard),
  PdrCreditRow: bind<ComponentProps<typeof PdrCreditRow>>(PdrCreditRow),
  OrderSummaryCard: bind<ComponentProps<typeof OrderSummaryCard>>(OrderSummaryCard),
  RosterActivityCard: bind<ComponentProps<typeof RosterActivityCard>>(RosterActivityCard),
  ChartPanel: bind<ComponentProps<typeof ChartPanel>>(ChartPanel),
  DataTable: bind<ComponentProps<typeof DataTable>>(DataTable)
};
